/**
 * Descriptor field-type → shared-form-component renderer (#997 — Story 3.1).
 *
 * Maps a `SectionDescriptorField.type` to the matching shared form component
 * (`FormInput` / `NumberInput` / `FormDropdown` / `YesNoSelector` /
 * `FormCheckbox`) and wires `field.validation` into an inline field error. This
 * is the generic edit body the descriptor engine renders — there is NO hand-
 * authored per-section field markup.
 *
 * Field visibility is computed from SEPARATE view-mode and RBAC inputs via
 * `isDescriptorFieldVisible` (never the `showFullDetails === false` forces-
 * REQUESTOR conflation), per Epic 3 D3.
 */

import React from 'react';
import { View, StyleSheet } from 'react-native';

import {
  descriptorFieldsInGroups,
  descriptorPerUnitFields,
  type SectionDescriptor,
  type SectionDescriptorField,
} from './sectionDescriptor.types';
import { FormCheckbox } from '../../components/forms/FormCheckbox';
import { FormDropdown } from '../../components/forms/FormDropdown';
import { FormInput } from '../../components/forms/FormInput';
import { NumberInput } from '../../components/forms/NumberInput';
import { YesNoSelector } from '../../components/forms/YesNoSelector';
import { LocationTagPicker } from '../../components/locationTags/LocationTagPicker';
import { spacing } from '../../theme/spacing';
import {
  isDescriptorFieldVisible,
  type DescriptorFieldVisibilityInput,
} from '../../utils/formFieldVisibility';
import { resolveRbacKeysForDataField } from '../fields/projections/summaryRows';

export type DescriptorFieldValue = string | boolean | number | null | undefined;
export type DescriptorEditableFieldValue = Exclude<
  DescriptorFieldValue,
  undefined
>;
export type DescriptorFieldValues = Record<string, DescriptorFieldValue>;

/**
 * Validate a single descriptor field value against its declarative rules.
 * Returns a human error string or `undefined` when valid. Pure and lazy (no
 * module-level regex/zod — the pattern is compiled per call to avoid the iOS
 * module-init crash class).
 */
export function validateDescriptorFieldValue(
  field: SectionDescriptorField,
  rawValue: DescriptorFieldValue,
): string | undefined {
  const rules = field.validation;
  if (!rules) {
    return undefined;
  }

  const isBlank =
    rawValue == null ||
    rawValue === '' ||
    (typeof rawValue === 'string' && rawValue.trim() === '');

  if (rules.required && isBlank) {
    return `${field.label} is required`;
  }
  if (isBlank) {
    return undefined; // optional + empty → valid
  }

  if (field.type === 'number') {
    const num = Number(rawValue);
    if (Number.isNaN(num)) {
      return `${field.label} must be a number`;
    }
    if (rules.min != null && num < rules.min) {
      return `${field.label} must be at least ${rules.min}`;
    }
    if (rules.max != null && num > rules.max) {
      return `${field.label} must be at most ${rules.max}`;
    }
  }

  if (typeof rawValue === 'string') {
    if (rules.maxLength != null && rawValue.length > rules.maxLength) {
      return `${field.label} must be ${rules.maxLength} characters or fewer`;
    }
    if (rules.options && !rules.options.includes(rawValue)) {
      return `${field.label} must be one of: ${rules.options.join(', ')}`;
    }
    if (rules.pattern && !new RegExp(rules.pattern.source).test(rawValue)) {
      return rules.pattern.message;
    }
  }

  return undefined;
}

/** Validate every per-unit field; returns a map of dataField → error message. */
export function validateDescriptorInstance(
  descriptor: SectionDescriptor,
  values: DescriptorFieldValues,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of descriptorPerUnitFields(descriptor)) {
    const error = validateDescriptorFieldValue(field, values[field.dataField]);
    if (error) {
      errors[field.dataField] = error;
    }
  }
  return errors;
}

function asString(value: DescriptorFieldValue): string {
  if (value == null) return '';
  return String(value);
}

/**
 * Map a stored `yesNo` value to the selector's editor form. A persisted BIT is a
 * boolean (or its string form), while the selector speaks `'Yes'` / `'No'`. A
 * blank / unknown value yields `''` (unselected). Pure and lazy — no module-level
 * state. Used by the editor control here and by the adapter's instance→field map.
 */
export function toYesNoValue(value: DescriptorFieldValue): string {
  if (value === true || value === 'true' || value === 'Yes') return 'Yes';
  if (value === false || value === 'false' || value === 'No') return 'No';
  return '';
}

/**
 * Map the selector's `'Yes'` / `'No'` editor value back to the stored boolean for
 * a `yesNo` field, so a BIT column round-trips (never the literal string `'Yes'`).
 * A blank / unselected value yields `undefined`.
 */
export function fromYesNoValue(
  value: DescriptorFieldValue,
): boolean | undefined {
  if (value === 'Yes' || value === true) return true;
  if (value === 'No' || value === false) return false;
  return undefined;
}

interface DescriptorFieldControlProps {
  field: SectionDescriptorField;
  value: DescriptorFieldValue;
  setField: (dataField: string, value: DescriptorEditableFieldValue) => void;
  disabled: boolean;
  error?: string;
  requestId?: string;
  requestSection?: string;
  isOnline?: boolean;
}

/** Render one descriptor field with the shared component for its `type`. */
export function DescriptorFieldControl({
  field,
  value,
  setField,
  disabled,
  error,
  requestId,
  requestSection,
  isOnline = true,
}: DescriptorFieldControlProps): React.ReactElement {
  const testID = `descriptor-field-${field.dataField}`;
  const common = {
    label: field.label,
    disabled,
    error,
    testID,
  };

  switch (field.type) {
    case 'locationTag':
      return (
        <LocationTagPicker
          requestId={requestId ?? 'new-request'}
          value={typeof value === 'number' ? value : null}
          onChange={next => setField(field.dataField, next)}
          disabled={disabled}
          testID={testID}
        />
      );
    case 'number':
      return (
        <NumberInput
          {...common}
          value={asString(value)}
          onChangeText={text => setField(field.dataField, text)}
          placeholder={field.placeholder}
        />
      );
    case 'dropdown':
      return (
        <FormDropdown
          {...common}
          value={asString(value)}
          options={[...(field.options ?? field.validation?.options ?? [])]}
          onSelect={selected =>
            setField(
              field.dataField,
              Array.isArray(selected) ? selected[0] ?? '' : selected,
            )
          }
          placeholder={field.placeholder}
        />
      );
    case 'yesNo':
      // YesNoSelector has no testID prop — pass explicit props (no spread).
      return (
        <YesNoSelector
          label={field.label}
          disabled={disabled}
          error={error}
          value={toYesNoValue(value)}
          onSelect={selected => setField(field.dataField, selected)}
        />
      );
    case 'checkbox':
      return (
        <FormCheckbox
          label={field.label}
          disabled={disabled}
          testID={testID}
          value={value === true}
          onValueChange={next => setField(field.dataField, next)}
        />
      );
    case 'multilineText':
      return (
        <FormInput
          {...common}
          value={asString(value)}
          onChangeText={text => setField(field.dataField, text)}
          placeholder={field.placeholder}
          multiline
          maxLength={field.maxLength ?? 10_000}
          richText={Boolean(requestSection)}
          requestId={requestId}
          requestSection={requestSection}
          isOnline={isOnline}
        />
      );
    case 'text':
    default:
      return (
        <FormInput
          {...common}
          value={asString(value)}
          onChangeText={text => setField(field.dataField, text)}
          placeholder={field.placeholder}
        />
      );
  }
}

export interface DescriptorEditFieldsProps {
  descriptor: SectionDescriptor;
  values: DescriptorFieldValues;
  setField: (dataField: string, value: DescriptorEditableFieldValue) => void;
  disabled: boolean;
  visibility: DescriptorFieldVisibilityInput;
  /**
   * Restrict rendering to per-unit fields in these `subsectionId` groups (#997
   * Story 6.3 editor-card partition). Omit to render every per-unit field.
   */
  fieldGroupKeys?: readonly string[];
  requestId?: string;
  isOnline?: boolean;
  testID?: string;
}

/**
 * The full generic per-unit edit body: every visible descriptor field rendered
 * from its `type`, with inline validation errors. Visibility derives from the
 * decoupled view-mode + RBAC inputs.
 */
export function DescriptorEditFields({
  descriptor,
  values,
  setField,
  disabled,
  visibility,
  fieldGroupKeys,
  requestId,
  isOnline = true,
  testID = 'descriptor-edit-fields',
}: DescriptorEditFieldsProps): React.ReactElement {
  const fields = fieldGroupKeys
    ? descriptorFieldsInGroups(descriptor, fieldGroupKeys)
    : descriptorPerUnitFields(descriptor);
  const errors = validateDescriptorInstance(descriptor, values);
  const inlineSection =
    descriptor.attachmentBaseSectionIds?.[0] ?? descriptor.stepId;
  // Invariant per section — resolve the anchor RBAC once, not per field.
  const anchorRbac = resolveRbacKeysForDataField(
    descriptor.permissionRef.rbacAnchorDataField,
  );

  return (
    <View style={styles.container} testID={testID}>
      {fields.map(field => {
        const mappingForField = resolveRbacKeysForDataField(field.dataField);
        const mapping = mappingForField ?? anchorRbac;
        // Fail closed: when neither the field nor the anchor resolves an RBAC
        // mapping, hide the field rather than passing empty (permissive)
        // section/field keys that would render it with no RBAC applied.
        if (!mapping) {
          return null;
        }
        const visible = isDescriptorFieldVisible(mapping, visibility);
        if (!visible) {
          return null;
        }
        return (
          <View key={field.dataField} style={styles.fieldRow}>
            <DescriptorFieldControl
              field={field}
              value={values[field.dataField]}
              setField={setField}
              disabled={disabled}
              error={errors[field.dataField]}
              requestId={requestId}
              requestSection={inlineSection}
              isOnline={isOnline}
            />
          </View>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  fieldRow: {
    marginBottom: spacing.fieldVerticalGap,
  },
});
